import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Hammer, Plus, Sparkles } from 'lucide-react';
import AuctionCard from '../components/AuctionCard';
import TimeFilter from '../components/TimeFilter';
import NewsBanner from '../components/NewsBanner';
import HierarchicalFilter from '../components/HierarchicalFilter';
import { SocketContext } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { toast } from 'react-hot-toast';
import { Auction } from '../types';
import { API_BASE_URL } from '../config/api';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';
import { isLikelyInAppWebView, prefersReducedMotion } from '../utils/runtimeEnvironment';
import { getHeroVideoUrlForPlayback, getHeroBackgroundImageSrc, getPublicAssetUrl } from '../utils/heroMedia';
import { useTranslation } from 'react-i18next';
import { unwrapApiData } from '../utils/apiResponse';
import LiveBidsTicker from '../components/LiveBidsTicker';
import LocationFilter from '../components/LocationFilter';
import MapRadiusModal from '../components/MapRadiusModal';
import { FILTER_ALL, isCanonicalCategorySlug, normalizeStoredCategoryToSlug } from '../constants/categories';
import { isAuctionActiveStatus, isAuctionSoldStatus } from '../utils/auctionStatusI18n';
import CategoryIconBar from '../components/CategoryIconBar';
import FilterDrawer from '../components/FilterDrawer';
import QuickBidModal from '../components/QuickBidModal';
import AppDownloadBadges from '../components/AppDownloadBadges';


const trimEndSlash = (s: string) => s.replace(/\/+$/, '');

type ListingTypeFilter = 'ALL' | 'AUCTION' | 'CLASSIFIED';

/** Legacy rows without an explicit type follow the DB default (AUCTION) */
const matchesListingType = (auction: Auction, filter: ListingTypeFilter) => {
  if (filter === 'ALL') return true;
  const rowType = auction.type === 'CLASSIFIED' ? 'CLASSIFIED' : 'AUCTION';
  return rowType === filter;
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const { heroVideoUrl, heroHomeText } = useSiteSettings();
  const [stats, setStats] = useState({
    totalAuctions: 0,
    activeAuctions: 0,
    soldAuctions: 0,
    totalBids: 0,
    pendingAuctions: 0
  });
  const [timeFilter, setTimeFilter] = useState('all');
  const [showNearMeMapPicker, setShowNearMeMapPicker] = useState(false);
  const [heroVideoReady, setHeroVideoReady] = useState(false);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const [priceSort, setPriceSort] = useState<'asc' | 'desc' | ''>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [quickBidAuction, setQuickBidAuction] = useState<Auction | null>(null);
  const [isQuickBidOpen, setIsQuickBidOpen] = useState(false);
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const skipHeroVideo = isLikelyInAppWebView() || prefersReducedMotion();
  const playableHeroVideoSrc = getHeroVideoUrlForPlayback(heroVideoUrl);
  const showHeroVideoPlayer = !skipHeroVideo && playableHeroVideoSrc != null;

  /** If `heroHomeText` is set (including explicit empty from admin), do not fall back to locale strings. */
  const heroOverlayCopy = React.useMemo(() => {
    if (!heroHomeText) {
      return {
        title: t('home.heroFallbackTitle'),
        desc: t('home.heroFallbackDesc'),
        cta: t('home.createCta')
      };
    }
    const lng = i18n.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const block = heroHomeText[lng] || heroHomeText.en;
    return {
      title: (block?.title ?? '').trim(),
      desc: (block?.desc ?? '').trim(),
      cta: (block?.cta ?? '').trim()
    };
  }, [heroHomeText, i18n.language, t]);

  const tryPlayHeroVideo = useCallback(() => {
    const el = heroVideoRef.current;
    if (!el) return;
    setHeroVideoFailed(false);
    // Remove el.load() which causes resets/flickering
    el.play()
      .then(() => setHeroVideoReady(true))
      .catch((err) => {
        console.warn('Hero video play blocked or failed:', err);
        setHeroVideoFailed(true);
      });
  }, []);

  useEffect(() => {
    setHeroVideoReady(false);
    setHeroVideoFailed(false);
  }, [playableHeroVideoSrc]);

  const category = React.useMemo(() => {
    const raw = searchParams.get('category');
    if (!raw || !raw.trim()) return FILTER_ALL;
    const decoded = decodeURIComponent(raw.trim());
    const mapped = normalizeStoredCategoryToSlug(decoded);
    if (mapped && mapped !== FILTER_ALL) return mapped;
    if (isCanonicalCategorySlug(decoded)) return decoded.trim().toLowerCase();
    return FILTER_ALL;
  }, [searchParams]);

  const subCategory = React.useMemo(() => {
    return searchParams.get('subCategory') || undefined;
  }, [searchParams]);

  const detailCategory = React.useMemo(() => {
    return searchParams.get('detailCategory') || undefined;
  }, [searchParams]);

  const leafId = React.useMemo(() => {
    return searchParams.get('leafId') || undefined;
  }, [searchParams]);

  const search = searchParams.get('search') || '';
  const city = searchParams.get('city') || '';
  const country = (searchParams.get('country') || '').trim().toUpperCase();

  const listingTypeFilter = React.useMemo<ListingTypeFilter>(() => {
    const raw = (searchParams.get('type') || '').trim().toUpperCase();
    return raw === 'AUCTION' || raw === 'CLASSIFIED' ? raw : 'ALL';
  }, [searchParams]);

  const handleListingTypeChange = useCallback((next: ListingTypeFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'ALL') {
      params.delete('type');
    } else {
      params.set('type', next);
    }
    params.set('page', '1');
    setSearchParams(params, { replace: true });
    setCurrentPage(1);
  }, [searchParams, setSearchParams]);

  const socketContext = useContext(SocketContext);
  const socket = socketContext?.socket;
  const { user } = useAuth();

  // دالة التحقق من تسجيل الدخول وإنشاء المزاد
  const handleCreateAuction = () => {
    if (!user) {
      toast.error(t('home.toastLoginCreate'));
      navigate('/login');
      return;
    }
    navigate('/create-auction');
  };

  const fetchStats = useCallback(async () => {
    try {
      const baseURL = API_BASE_URL;
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const statsUrl =
        country && /^[A-Z]{2}$/.test(country)
          ? `${baseURL}/stats?country=${encodeURIComponent(country)}`
          : `${baseURL}/stats`;
      const response = await axios.get(statsUrl, { headers });
      const raw = response?.data;
      const s =
        raw && typeof raw === 'object' && raw.success === true && raw.data && typeof raw.data === 'object'
          ? raw.data
          : raw && typeof raw === 'object'
            ? raw
            : {};
      const next = {
        totalAuctions: Number(s.totalAuctions ?? 0) || 0,
        activeAuctions: Number(s.activeAuctions ?? 0) || 0,
        soldAuctions: Number(s.soldAuctions ?? 0) || 0,
        totalBids: Number(s.totalBids ?? 0) || 0,
        pendingAuctions: Number(s.pendingAuctions ?? 0) || 0
      };
      setStats(next);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [country]);

  const fetchAuctions = useCallback(async () => {
    try {
      const baseURL = trimEndSlash(API_BASE_URL);
      const params = new URLSearchParams({
        category: category === FILTER_ALL ? '' : category,
        search,
        page: currentPage.toString(),
        limit: '50',
        timeFilter: timeFilter
      });
      if (subCategory) params.append('subCategory', subCategory);
      if (detailCategory) params.append('detailCategory', detailCategory);
      if (leafId) params.append('leafId', leafId);
      if (city) params.append('city', city);
      if (country) params.append('country', country);
      if (listingTypeFilter !== 'ALL') params.append('type', listingTypeFilter);
      
      const lat = searchParams.get('lat');
      const lng = searchParams.get('lng');
      const radiusKm = searchParams.get('radiusKm');
      const sellerId = searchParams.get('sellerId');
      
      if (sellerId) params.append('sellerId', sellerId);
      
      if (lat && lng) {
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('radiusKm', radiusKm || '30');
      }

      searchParams.forEach((val, key) => {
        if (key.startsWith('attr_')) {
          params.append(key, val);
        }
      });

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${baseURL}/auctions?${params.toString()}`, { headers });
      const listBody = unwrapApiData<{
        auctions?: Auction[];
        pagination?: { hasNext?: boolean; hasPrev?: boolean };
      }>(response.data);
      const rawList =
        listBody.auctions ?? (response.data as { auctions?: Auction[] }).auctions ?? [];
      const filteredAuctions = rawList.filter((a: any) => a && a.id && a.title);
      
      // إخفاء المزادات المباعة بعد 10 دقائق من انتهائها (للبائع والمشتري فقط)
      const now = Date.now();
      const tenMinutes = 10 * 60 * 1000;
      const visibleAuctions = filteredAuctions.filter((auction: Auction) => {
        // إذا كان المزاد نشط، اعرضه
        if (isAuctionActiveStatus(auction.status)) {
          return true;
        }
        
        // إذا كان المزاد مباع، تحقق من الوقت والمستخدم
        if (isAuctionSoldStatus(auction.status)) {
          const endTime = new Date(auction.endTime || 0).getTime();
          const timeSinceEnd = now - endTime;
          
          // إذا مر أكثر من 10 دقائق، لا تعرض المزاد لأي أحد
          if (timeSinceEnd > tenMinutes) {
            return false;
          }
          
          // إذا لم يمر 10 دقائق، اعرض المزاد فقط للبائع والمشتري
          if (user) {
            const isSeller = auction.seller && auction.seller.id === user.id;
            const isWinner = auction.winner && auction.winner.id === user.id;
            return isSeller || isWinner;
          }
          
          // إذا لم يكن المستخدم مسجل دخول، لا تعرض المزاد المباع
          return false;
        }
        
        // للمزادات الأخرى، اعرضها
        return true;
      });
      
      setAuctions(visibleAuctions);
      const pag =
        listBody.pagination ?? (response.data as { pagination?: { hasNext?: boolean; hasPrev?: boolean } }).pagination;
      setHasNext(!!pag?.hasNext);
      setHasPrev(!!pag?.hasPrev);
    } catch (error) {
      console.error('Failed to fetch auctions:', error);
      setAuctions([]);
    } finally {
      setLoading(false);
    }
  }, [category, subCategory, detailCategory, leafId, search, currentPage, user, timeFilter, city, country, listingTypeFilter, searchParams]);
  
  const handleCategoryChange = (catId: string, sub?: string, detail?: string, leaf?: string) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (catId === FILTER_ALL) {
        p.delete('category');
        p.delete('subCategory');
        p.delete('detailCategory');
        p.delete('leafId');
      } else {
        p.set('category', catId);
        if (sub) p.set('subCategory', sub);
        else p.delete('subCategory');
        
        if (detail) p.set('detailCategory', detail);
        else p.delete('detailCategory');

        if (leaf) p.set('leafId', leaf);
        else p.delete('leafId');
      }
      p.set('page', '1');
      return p;
    });
    // On mobile, close drawer if it was open
    setIsFilterDrawerOpen(false);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('category');
      p.delete('subCategory');
      p.delete('detailCategory');
      p.delete('leafId');
      p.delete('type');
      p.set('page', '1');
      return p;
    });
    setTimeFilter('all');
    setCurrentPage(1);
  };

  const handleTimeFilterChange = (newTimeFilter: string) => {
    setTimeFilter(newTimeFilter);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchSponsoredAuctions = useCallback(async () => {
    try {
      const baseURL = API_BASE_URL;
      const res = await axios.get(`${baseURL}/auctions?isSponsored=true&limit=6`);
      const body = unwrapApiData<{ auctions?: Auction[] }>(res.data);
      return body.auctions ?? [];
    } catch {
      return [];
    }
  }, []);

  const [sponsoredAuctions, setSponsoredAuctions] = useState<Auction[]>([]);

  useEffect(() => {
    fetchSponsoredAuctions().then(setSponsoredAuctions);
  }, [fetchSponsoredAuctions]);

  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // انضمام لغرفة الصفحة الرئيسية للسوكيت لاستقبال تحديثات المزايدات المباشرة
  useEffect(() => {
    if (!socket) return;
    
    const joinRoom = () => {
      // console.log('HomePage: Joining homepage room'); // تم تعطيله
      socket.emit('join_homepage');
    };

    const handleNewAd = (newAd: any) => {
      setAuctions(prev => [newAd, ...prev]);
    };

    const handleBidUpdate = (data: any) => {
      const targetId = data.auctionId;
      const patchData = data.patch || { currentPrice: data.currentPrice, endTime: data.endDate || data.endTime };
      
      setAuctions(prev => prev.map(a => {
        if (String(a.id) === String(targetId)) {
          const newBidCount = data.bidCount ?? (a.bidCount || 0) + 1;
          return { ...a, ...patchData, bidCount: newBidCount };
        }
        return a;
      }));
    };

    const handleAuctionUpdated = (data: any) => {
      setAuctions(prev => {
        return prev.map(a => {
          if (String(a.id) === String(data.auctionId)) {
            return { ...a, status: data.status, winnerId: data.winner };
          }
          return a;
        }).filter(a => {
          if (String(a.id) === String(data.auctionId) && (data.status === 'منتهي' || data.status === 'غير مباع')) {
             return user && (String(a.sellerId) === String(user.id) || String(data.winner) === String(user.id));
          }
          return true;
        });
      });
    };

    // انضم فوراً إذا كان السوكيت متصلاً بالفعل
    if (socket.connected) {
      joinRoom();
    }

    // انضم عند كل اتصال جديد أو إعادة اتصال
    socket.on('connect', joinRoom);
    socket.on('new_ad', handleNewAd);
    socket.on('bid_update', handleBidUpdate);
    socket.on('auction_updated', handleAuctionUpdated);
    
    return () => {
      socket.off('connect', joinRoom);
      socket.off('new_ad', handleNewAd);
      socket.off('bid_update', handleBidUpdate);
      socket.off('auction_updated', handleAuctionUpdated);
      socket.emit('leave_homepage');
    };
  }, [socket, user]);

  const filteredActiveAuctions = auctions.filter(a => {
    if (a.isDealConfirmed) return false;
    if (!matchesListingType(a, listingTypeFilter)) return false;
    // Time filter logic
    if (timeFilter !== 'all' && a.endTime) {
      const timeDiff = new Date(a.endTime).getTime() - Date.now();
      if (timeDiff <= 0) return false;
      if (timeFilter === 'ending_soon' && timeDiff > 3 * 60 * 60 * 1000) return false; // less than 3 hours
      if (timeFilter === '1hour' && timeDiff > 60 * 60 * 1000) return false;
      if (timeFilter === '24hours' && timeDiff > 24 * 60 * 60 * 1000) return false;
      if (timeFilter === '1week' && timeDiff > 7 * 24 * 60 * 60 * 1000) return false;
      if (timeFilter === '1month' && timeDiff > 30 * 24 * 60 * 60 * 1000) return false;
    }
    
    return true;
  });

  if (priceSort === 'asc') {
    filteredActiveAuctions.sort((a, b) => (a.currentPrice || a.startingPrice || 0) - (b.currentPrice || b.startingPrice || 0));
  } else if (priceSort === 'desc') {
    filteredActiveAuctions.sort((a, b) => (b.currentPrice || b.startingPrice || 0) - (a.currentPrice || a.startingPrice || 0));
  }

  /** Listing order comes from API (sponsored, last bid, ending soon); interleave adds extra sponsored slots */
  const interleavedAuctions = React.useMemo(() => {
    if (sponsoredAuctions.length === 0) return filteredActiveAuctions;
    const mainIds = new Set(filteredActiveAuctions.map((a) => a.id));
    /** لا تُدرج كرت مموّل إن كان نفس المزاد ظاهراً بالفعل في الشبكة (تجنّب تكرار بصري) */
    const seenSp = new Set<number | string>();
    const sponsoredOnly = sponsoredAuctions.filter((sp) => {
      if (!sp?.id || mainIds.has(sp.id)) return false;
      if (!matchesListingType(sp, listingTypeFilter)) return false;
      if (seenSp.has(sp.id)) return false;
      seenSp.add(sp.id);
      return true;
    });
    if (sponsoredOnly.length === 0) return filteredActiveAuctions;

    const merged: (Auction & { isInterleavedSponsored?: boolean })[] = [...filteredActiveAuctions];
    sponsoredOnly.forEach((sp, idx) => {
      const pos = (idx + 1) * 3 + idx;
      if (pos < merged.length) {
        merged.splice(pos, 0, { ...sp, isInterleavedSponsored: true });
      }
    });
    return merged;
  }, [filteredActiveAuctions, sponsoredAuctions, listingTypeFilter]);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 overflow-x-clip w-full transition-colors duration-500">
      <section className="relative overflow-hidden rounded-b-[2.5rem] mb-6 shadow-2xl isolate border-b border-white/10 hidden lg:block">
        <div className="relative w-full aspect-[21/9] max-h-[280px] min-h-[180px] bg-slate-900">
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div
              className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${getHeroBackgroundImageSrc()})` }}
            />
            {!showHeroVideoPlayer ? (
              <div className="absolute inset-0 z-[1] bg-slate-900/25">
                <div
                  className="absolute start-3 top-3 h-14 w-14 bg-contain bg-center bg-no-repeat opacity-90 sm:start-4 sm:top-4 sm:h-16 sm:w-16"
                  style={{ backgroundImage: `url(${getPublicAssetUrl(BRAND_LOGO_PUBLIC_PATH)})` }}
                  role="img"
                  aria-hidden
                />
              </div>
            ) : (
              <>
                <video
                ref={heroVideoRef}
                className={`absolute inset-0 z-[2] h-full w-full min-h-0 min-w-0 object-cover object-center ${heroVideoReady ? 'opacity-100' : 'opacity-0'}`}
                src={playableHeroVideoSrc}
                muted
                loop
                playsInline
                autoPlay
                onLoadedData={() => tryPlayHeroVideo()}
                onError={() => setHeroVideoFailed(true)}
                style={{
                  transition: 'opacity 1.5s ease-in-out',
                  backgroundImage: `url(${getHeroBackgroundImageSrc()})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
                {!heroVideoReady && !heroVideoFailed && (
                  <div
                    className="absolute inset-0 z-[1] bg-cover bg-center opacity-90 transition-opacity duration-500"
                    style={{ backgroundImage: `url(${getPublicAssetUrl(BRAND_LOGO_PUBLIC_PATH)})` }}
                  />
                )}
                {heroVideoFailed && (
                  <div
                    className="absolute inset-0 z-[1] bg-cover bg-center opacity-75"
                    style={{ backgroundImage: `url(${getPublicAssetUrl(BRAND_LOGO_PUBLIC_PATH)})` }}
                  />
                )}
              </>
            )}
          </div>
          {/* Text centered; CTA in corner (logical end/bottom) so it does not compete with headline */}
          <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-slate-900/25 via-transparent to-slate-900/45">
            <div
              className={`flex h-full flex-col items-center justify-center px-4 pt-8 text-center sm:px-6 lg:px-8 ${
                heroOverlayCopy.cta ? 'pb-14 sm:pb-16' : 'pb-8'
              }`}
            >
              {heroOverlayCopy.title ? (
                <h1 className="mb-3 text-3xl font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)] sm:text-4xl md:text-5xl">
                  {heroOverlayCopy.title}
                </h1>
              ) : null}
              {heroOverlayCopy.desc ? (
                <p className="max-w-2xl text-base leading-relaxed text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)] sm:text-lg md:text-xl">
                  {heroOverlayCopy.desc}
                </p>
              ) : null}
            </div>
            <AppDownloadBadges className="pointer-events-auto absolute bottom-3 start-3 z-30 sm:bottom-4 sm:start-4" />
            {heroOverlayCopy.cta ? (
              <button
                type="button"
                onClick={handleCreateAuction}
                className="pointer-events-auto absolute bottom-3 end-3 z-30 inline-flex max-w-[min(100%,18rem)] items-center gap-1.5 rounded-lg border border-yellow-300/90 bg-gradient-to-r from-yellow-400 to-orange-500 px-3 py-2 text-xs font-semibold text-white shadow-md transition hover:from-yellow-500 hover:to-orange-600 hover:shadow-lg sm:bottom-4 sm:end-4 sm:gap-2 sm:px-3.5 sm:py-2 sm:text-sm"
              >
                <Plus className="h-4 w-4 shrink-0 sm:h-4 sm:w-4" />
                <span className="truncate text-start leading-tight">{heroOverlayCopy.cta}</span>
                <Sparkles className="h-3.5 w-3.5 shrink-0 opacity-90 sm:h-4 sm:w-4" />
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* Adaptive Mobile Filtering Bar (Icon-first) */}
      <div className="lg:hidden flex items-center justify-between mb-0.5 lg:mb-2">
        <div className="flex-1 overflow-hidden">
          <CategoryIconBar 
            selectedCategory={category}
            onCategoryChange={(id) => handleCategoryChange(id)}
            onOpenFilter={() => setIsFilterDrawerOpen(true)}
          />
        </div>
        {(category || subCategory || detailCategory || searchParams.has('brand') || searchParams.has('city') || searchParams.has('lat') || timeFilter !== 'all' || listingTypeFilter !== 'ALL') && (
          <button
            onClick={handleClearFilters}
            className="ms-2 me-2 shrink-0 bg-red-100/80 text-red-600 px-2 py-1 rounded-md shadow-sm text-[10px] font-bold flex items-center gap-1 active:scale-95 transition-transform"
          >
            <span className="w-3 h-3 flex items-center justify-center text-[10px]">🔄</span>
            <span>تفريغ</span>
          </button>
        )}
      </div>

      {/* Main Live Bids Ticker (Now hidden on mobile per user request, thinner) */}
      <div className="hidden lg:block w-full max-w-[1600px] mx-auto px-2 sm:px-6 lg:px-8 mt-2 mb-2 lg:mb-4">
        <LiveBidsTicker />
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-6 lg:px-8 py-0 lg:py-4">
        <div className="mt-0 lg:mt-6 flex flex-col gap-2 lg:gap-6 lg:flex-row">
          {/* عمود التصنيفات عمودياً بجانب المحتوى (مثل التخطيط السابق) */}
          <aside className="hidden lg:block w-72 shrink-0 animate-fadeInLeft">
            <Link
              to="/"
              aria-label={t('nav.home')}
              title={t('nav.home')}
              className="group mb-3 flex items-center gap-3 rounded-2xl bg-white/10 p-1.5 shadow-xl ring-1 ring-white/30 backdrop-blur-md transition-all duration-500 hover:ring-primary-400/60 dark:bg-slate-700/50 dark:ring-slate-600/50 dark:hover:shadow-primary-500/20"
            >
              <img
                src={BRAND_LOGO_PUBLIC_PATH}
                alt={t('header.logoAlt')}
                className="h-10 w-10 shrink-0 rounded-full object-contain transition-transform duration-500 group-hover:scale-110 sm:h-12 sm:w-12"
              />
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-sm font-extrabold text-slate-800 dark:text-white">
                  {t('brand')}
                </span>
                <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                  {t('nav.home')}
                </span>
              </span>
            </Link>
            <div className="sticky top-20 max-h-[min(80vh,calc(100vh-5.5rem))] overflow-y-auto overflow-x-hidden rounded-2xl border border-slate-200/50 bg-white/70 p-3 shadow-xl ring-1 ring-white/20 backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-800/60">
              <HierarchicalFilter
                selectedCategory={category}
                selectedSubCategory={subCategory}
                selectedDetailCategory={detailCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          </aside>

          {/* On small screens: We now use a Drawer for the deep tree to save space */}
          <FilterDrawer 
            isOpen={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            selectedCategory={category}
            selectedSubCategory={subCategory}
            selectedDetailCategory={detailCategory}
            onCategoryChange={handleCategoryChange}
            onClear={handleClearFilters}
          />

          <main className="flex min-w-0 flex-1 flex-col gap-2 lg:gap-4">
             <div className="hidden lg:flex flex-row items-center justify-end gap-4 pb-3 border-b border-slate-200 dark:border-slate-700/60">
                <div className="hidden md:flex w-full min-w-0 shrink-0 justify-center lg:w-auto lg:justify-end">
                   <div className="grid w-full max-w-xl grid-cols-2 gap-1.5 rounded-xl border border-slate-200/40 bg-white/40 px-2 py-1.5 shadow-inner ring-1 ring-white/10 backdrop-blur-md dark:border-slate-700/40 dark:bg-slate-800/40 sm:max-w-none md:inline-grid md:w-max md:grid-cols-5 md:gap-1.5 md:px-2 md:py-1.5">
                      <div className="text-center group">
                        <div className="text-base font-black text-blue-500 transition-transform group-hover:scale-105 sm:text-lg">{stats.totalAuctions}</div>
                        <div className="text-[8px] font-bold uppercase leading-tight tracking-tighter text-slate-500 sm:text-[9px]">{t('home.stats.total')}</div>
                      </div>
                      <div className="text-center group">
                        <div className="text-base font-black text-amber-500 transition-transform group-hover:scale-105 sm:text-lg">{stats.pendingAuctions ?? 0}</div>
                        <div className="text-[8px] font-bold uppercase leading-tight tracking-tighter text-slate-500 sm:text-[9px]">{t('home.stats.pending')}</div>
                      </div>
                      <div className="text-center group">
                        <div className="text-base font-black text-emerald-500 transition-transform group-hover:scale-105 sm:text-lg">{stats.activeAuctions}</div>
                        <div className="text-[8px] font-bold uppercase leading-tight tracking-tighter text-slate-500 sm:text-[9px]">{t('home.stats.active')}</div>
                      </div>
                      <div className="text-center group">
                        <div className="text-base font-black text-purple-500 transition-transform group-hover:scale-105 sm:text-lg">{stats.soldAuctions}</div>
                        <div className="text-[8px] font-bold uppercase leading-tight tracking-tighter text-slate-500 sm:text-[9px]">{t('home.stats.sold')}</div>
                      </div>
                      <div className="text-center group">
                        <div className="text-base font-black text-orange-500 transition-transform group-hover:scale-105 sm:text-lg">{stats.totalBids}</div>
                        <div className="text-[8px] font-bold uppercase leading-tight tracking-tighter text-slate-500 sm:text-[9px]">{t('home.stats.bids')}</div>
                      </div>
                   </div>
                </div>
             </div>

             <div className="sticky top-[84px] lg:top-20 z-10 mt-0 mb-1 sm:mb-4 bg-slate-100/90 dark:bg-slate-900/90 px-1 sm:px-4 py-1 sm:py-2 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
               <div className="grid grid-cols-4 gap-1 w-full">
                 
                 {/* 1. Tabs */}
                 <select
                   value={listingTypeFilter}
                   onChange={(e) => handleListingTypeChange(e.target.value as ListingTypeFilter)}
                   className="h-8 !text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md outline-none appearance-none flex-1 text-center truncate"
                 >
                   <option value="ALL">النوع</option>
                   <option value="AUCTION">مزادات</option>
                   <option value="CLASSIFIED">إعلانات</option>
                 </select>
                 
                 {/* 2. Time Filter */}
                 <select
                   value={timeFilter}
                   onChange={(e) => handleTimeFilterChange(e.target.value)}
                   className="h-8 !text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md outline-none appearance-none flex-1 text-center truncate"
                 >
                   <option value="all">كل الأوقات</option>
                   <option value="ending_soon">ينتهي قريباً</option>
                   <option value="1hour">أقل من ساعة</option>
                   <option value="24hours">أقل من يوم</option>
                   <option value="1week">أقل من أسبوع</option>
                   <option value="1month">أقل من شهر</option>
                 </select>
                 
                 {/* 3. Location Filter */}
                 <select
                   value={searchParams.has('lat') ? 'map' : (searchParams.has('city') ? 'near-me' : 'all')}
                   onChange={(e) => {
                     const val = e.target.value;
                     if (val === 'map') {
                       setShowNearMeMapPicker(true);
                     } else if (val === 'near-me') {
                       if (user && user.city) {
                         setSearchParams(prev => {
                           const p = new URLSearchParams(prev);
                           p.set('city', user.city || '');
                           p.delete('lat');
                           p.delete('lng');
                           p.delete('radiusKm');
                           return p;
                         });
                       } else {
                         if (!navigator.geolocation) {
                           toast.error(t('home.geoNotSupported', 'الموقع غير مدعوم'));
                           return;
                         }
                         toast.loading(t('home.locating', 'جاري التحديد...'), { duration: 2000 });
                         navigator.geolocation.getCurrentPosition((pos) => {
                           setSearchParams(prev => {
                             const p = new URLSearchParams(prev);
                             p.set('lat', String(pos.coords.latitude));
                             p.set('lng', String(pos.coords.longitude));
                             p.set('radiusKm', '30');
                             p.delete('city');
                             return p;
                           });
                         }, () => {
                           toast.error(t('home.geoFailed', 'فشل التحديد'));
                         });
                       }
                     } else {
                       setSearchParams(prev => {
                         const p = new URLSearchParams(prev);
                         p.delete('lat');
                         p.delete('lng');
                         p.delete('radiusKm');
                         p.delete('city');
                         return p;
                       });
                     }
                   }}
                   className="h-8 !text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md outline-none appearance-none flex-1 text-center truncate"
                 >
                   <option value="all">{t('home.location.all', 'كل المناطق')}</option>
                   <option value="near-me">{t('home.location.nearMe', 'قريب مني')}</option>
                   <option value="map">{t('home.location.map', 'على الخريطة')}</option>
                 </select>

                 {/* 4. Sort */}
                 <select
                    value={priceSort}
                    onChange={(e) => {
                       setPriceSort(e.target.value as any);
                       setCurrentPage(1);
                    }}
                    className="h-8 !text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md outline-none appearance-none flex-1 text-center truncate"
                 >
                  <option value="">{t('home.sort.default', 'الترتيب الافتراضي')}</option>
                  <option value="asc">{t('home.sort.priceAsc', 'السعر تصاعدي')}</option>
                  <option value="desc">{t('home.sort.priceDesc', 'السعر تنازلي')}</option>
                 </select>
               </div>
             </div>

             <MapRadiusModal
                isOpen={showNearMeMapPicker}
                onClose={() => setShowNearMeMapPicker(false)}
                onConfirm={(lat, lng, radius) => {
                  setSearchParams(prev => {
                    const p = new URLSearchParams(prev);
                    p.set('lat', String(lat));
                    p.set('lng', String(lng));
                    p.set('radiusKm', String(radius));
                    p.delete('city');
                    return p;
                  });
                  setShowNearMeMapPicker(false);
                }}
                initialLat={Number(searchParams.get('lat')) || undefined}
                initialLng={Number(searchParams.get('lng')) || undefined}
                initialRadius={Number(searchParams.get('radiusKm')) || 30}
              />

             {searchParams.get('sellerId') && (
               <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 p-3 sm:p-4 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                 <div className="flex items-center gap-2">
                   <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 shrink-0" />
                   <span className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-300 leading-tight">
                     يتم عرض إعلانات بائع محدد
                   </span>
                 </div>
                 <button
                   onClick={() => {
                     const next = new URLSearchParams(searchParams);
                     next.delete('sellerId');
                     setSearchParams(next);
                   }}
                   className="shrink-0 text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                 >
                   إلغاء التصفية
                 </button>
               </div>
             )}

             {loading ? (
               <div className="flex justify-center items-center py-20">
                 <div className="spinner w-12 h-12 border-t-primary-500"></div>
               </div>
             ) : interleavedAuctions.length > 0 ? (
               <>
               <p className="hidden md:flex mb-4 items-start gap-2 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs leading-relaxed text-amber-950 dark:border-amber-500/25 dark:bg-amber-950/30 dark:text-amber-100/95">
                 <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                 <span>{t('home.sponsored.gridHint')}</span>
               </p>
               <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-6">
                 {interleavedAuctions.map((item: Auction & { isInterleavedSponsored?: boolean }, idx) => (
                    <div 
                     key={item.isInterleavedSponsored ? `sp-${item.id}-${idx}` : item.id}
                     className={item.isNew ? 'auction-appear' : ''}
                    >
                     <AuctionCard auction={item} onQuickBid={(a) => {
                       setQuickBidAuction(a);
                       setIsQuickBidOpen(true);
                     }} />
                    </div>
                 ))}
               </div>
               </>
             ) : (
               <div className="text-center py-20 bg-slate-50/50 dark:bg-slate-800/20 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                 {listingTypeFilter === 'CLASSIFIED' ? (
                   <span className="text-6xl mb-4 block">📢</span>
                 ) : listingTypeFilter === 'AUCTION' ? (
                   <span className="text-6xl mb-4 block">🔨</span>
                 ) : (
                   <Hammer className="w-20 h-20 mx-auto text-slate-300 dark:text-slate-700 mb-4" />
                 )}
                 <h3 className="text-xl font-bold text-slate-400">
                   {listingTypeFilter === 'CLASSIFIED' 
                     ? t('home.empty.classifieds', 'لا توجد إعلانات متاحة') 
                     : listingTypeFilter === 'AUCTION'
                       ? t('home.empty.auctions', 'لا توجد مزادات متاحة')
                       : t('home.empty.title')
                   }
                 </h3>
               </div>
             )}

             {(hasNext || hasPrev) && (
               <div className="flex justify-center items-center gap-4 mt-12 bg-white/50 dark:bg-slate-800/50 backdrop-blur-md p-4 rounded-2xl border border-slate-200 dark:border-slate-700 w-fit mx-auto shadow-sm">
                 <button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={!hasPrev} 
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 transition-all font-bold text-sm"
                  title={t('home.pagination.prev')}
                 >
                   {t('home.pagination.prev')}
                 </button>
                 <span className="font-black text-primary-600 dark:text-primary-400 px-4">{currentPage}</span>
                 <button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={!hasNext} 
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 transition-all font-bold text-sm"
                  title={t('home.pagination.next')}
                 >
                   {t('home.pagination.next')}
                 </button>
               </div>
             )}
          </main>

          <aside className="hidden xl:block w-80 shrink-0 space-y-6 animate-fadeInRight">
             <div className="bg-gradient-to-br from-primary-600 to-indigo-700 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden group">
                <div className="relative z-10">
                   <h3 className="text-xl font-bold mb-2">🎁 {t('home.donationBanner.title')}</h3>
                   <p className="text-sm opacity-90 leading-relaxed mb-4">{t('home.donationBanner.body')}</p>
                   <button className="w-full py-3 bg-white text-primary-700 font-bold rounded-xl shadow-lg ring-1 ring-white/20 hover:scale-[1.02] transition-transform">
                      {t('home.viewDetails')}
                   </button>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
             </div>

             <div className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-lg p-5">
                <h4 className="flex items-center gap-2 font-bold mb-4 text-slate-800 dark:text-slate-100 uppercase tracking-widest text-xs">
                   <Sparkles className="w-4 h-4 text-amber-500" />
                   {t('home.announcements')}
                </h4>
                <NewsBanner />
             </div>
          </aside>
        </div>
      </div>

      {quickBidAuction && (
        <QuickBidModal
          auction={quickBidAuction}
          isOpen={isQuickBidOpen}
          onClose={() => setIsQuickBidOpen(false)}
          onSuccess={(updated) => {
            setAuctions(prev => prev.map(a => a.id === updated.id ? { ...a, currentPrice: updated.currentPrice, bidCount: updated.bidCount } : a));
          }}
        />
      )}
    </div>
  );
};

export default HomePage;
